import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  createCategory, 
  updateCategory, 
  getAllCategories 
} from '../../firebase/models/blogService';

const Container = styled.div`
  padding: var(--spacing-xl);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
`;

const Title = styled.h1`
  margin: 0;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const Input = styled.input`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Textarea = styled.textarea`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const Button = styled.button`
  padding: var(--spacing-md);
  background-color: ${props => props.secondary ? '#f0f0f0' : 'var(--accent-blue)'};
  color: ${props => props.secondary ? 'inherit' : 'white'};
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.secondary ? '#e0e0e0' : 'var(--dark-blue)'};
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  padding: var(--spacing-md);
  background-color: ${props => props.type === 'error' ? '#ffeeee' : '#eeffee'};
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const CategoryEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Fetch category data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        try {
          const categories = await getAllCategories();
          const category = categories.find(cat => cat.id === id);
          
          if (!category) {
            setError('Category not found');
            return;
          }
          
          setFormData({
            name: category.name || '',
            slug: category.slug || '',
            description: category.description || ''
          });
        } catch (err) {
          console.error('Error fetching category:', err);
          setError('Failed to load category data. Please try again.');
        }
      }
    };
    
    fetchData();
  }, [id, isEditMode]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-generate slug when name changes
    if (name === 'name' && (!formData.slug || formData.slug === generateSlug(formData.name))) {
      setFormData({
        ...formData,
        name: value,
        slug: generateSlug(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (isEditMode) {
        await updateCategory(id, formData);
        setSuccess('Category updated successfully!');
      } else {
        await createCategory(formData);
        setSuccess('Category created successfully!');
        
        // Reset form after successful creation
        setFormData({
          name: '',
          slug: '',
          description: ''
        });
      }
      
      // Navigate back to category list after a short delay
      setTimeout(() => {
        navigate('/admin/categories');
      }, 2000);
      
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>{isEditMode ? 'Edit Category' : 'Create New Category'}</Title>
      </Header>
      
      {error && <Message type="error">{error}</Message>}
      {success && <Message>{success}</Message>}
      
      <Card>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </FormGroup>
          
          <ButtonContainer>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Category' : 'Create Category'}
            </Button>
            <Button 
              type="button" 
              secondary 
              onClick={() => navigate('/admin/categories')}
            >
              Cancel
            </Button>
          </ButtonContainer>
        </Form>
      </Card>
    </Container>
  );
};

export default CategoryEditor; 